import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';
import { useSchemas, useMyDIDs } from '../../api/hooks';
import IssuerSignerPicker from '../../components/IssuerSignerPicker';
import { issueSignedCredential, type IssuerSigner } from '../../lib/credentialSigning';

interface CsvRow {
  id: number;
  data: Record<string, string>;
  status: 'valid' | 'error' | 'pending' | 'issued';
  error?: string;
}

export default function BulkIssuance() {
  // Only the issuer's own active schemas, as on single issuance.
  const { data: allMine, loading: schemasLoading } = useSchemas('mine');
  const schemas = (allMine ?? []).filter((s) => !s.deactivated);
  const { data: myDIDs } = useMyDIDs();
  const defaultIssuerDid = (myDIDs ?? []).find((d) => d.status === 'active')?.did ?? (myDIDs ?? [])[0]?.did;
  const [signer, setSigner] = useState<IssuerSigner | null>(null);
  const { t } = useTranslation('issuer');
  const [selectedSchema, setSelectedSchema] = useState('');
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const parseCsv = (text: string) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return;
    const headers = lines[0].split(',').map((h) => h.trim());
    const rows: CsvRow[] = lines.slice(1).map((line, idx) => {
      const values = line.split(',').map((v) => v.trim());
      const data: Record<string, string> = {};
      headers.forEach((h, i) => { data[h] = values[i] ?? ''; });
      const hasHolder = !!data['holderDid'];
      const hasEmptyRequired = Object.entries(data).some(([, v]) => !v);
      return {
        id: idx + 1,
        data,
        status: !hasHolder ? 'error' as const : hasEmptyRequired ? 'error' as const : 'valid' as const,
        error: !hasHolder ? t('bulkIssuance.errors.missingHolder') : hasEmptyRequired ? t('bulkIssuance.errors.missingField') : undefined,
      };
    });
    setCsvRows(rows);
  };

  const handleFile = (e?: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      if (text) parseCsv(text);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        if (text) parseCsv(text);
      };
      reader.readAsText(file);
    }
  };

  const validCount = csvRows.filter((r) => r.status === 'valid' || r.status === 'issued').length;
  const errorCount = csvRows.filter((r) => r.status === 'error').length;
  const issuedCount = csvRows.filter((r) => r.status === 'issued').length;

  const schema = (schemas ?? []).find((s) => s.id === selectedSchema);
  // Issue as the DID that authored the schema, as single issuance does.
  const issuerDid = schema?.author ?? defaultIssuerDid;

  const handleBulkIssue = async () => {
    if (!signer || !issuerDid) return;
    setIsProcessing(true);
    setProgress(0);

    const validRows = csvRows.filter((r) => r.status === 'valid');
    let issued = 0;

    for (const row of validRows) {
      try {
        const { holderDid, ...fields } = row.data;
        await issueSignedCredential({
          issuer: issuerDid,
          schemaId: selectedSchema,
          subjectDid: holderDid,
          type: ['VerifiableCredential', schema?.name ?? ''],
          credentialSubject: Object.fromEntries(
            Object.entries(fields).map(([k, v]) => {
              const fieldDef = schema?.fields.find((f) => f.name === k);
              if (fieldDef?.type === 'number') return [k, Number(v)];
              if (fieldDef?.type === 'boolean') return [k, v === 'true' || v === '1' || v === 'yes'];
              return [k, v];
            }),
          ),
        }, signer, issuerDid);
        setCsvRows((rows) =>
          rows.map((r) => r.id === row.id ? { ...r, status: 'issued' as const, error: undefined } : r)
        );
      } catch (e) {
        // Keep the row and say why, so the issuer can fix it and retry.
        const msg = e instanceof Error ? e.message : String(e);
        setCsvRows((rows) =>
          rows.map((r) => r.id === row.id ? { ...r, status: 'error' as const, error: msg } : r)
        );
        // A refused wallet signature stops the batch rather than prompting
        // again for every remaining row.
        if (/user (rejected|denied)|ACTION_REJECTED/i.test(msg)) break;
      }
      issued++;
      setProgress(Math.round((issued / validRows.length) * 100));
    }

    setIsProcessing(false);
    setCompleted(true);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('bulkIssuance.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('bulkIssuance.subtitle')}</p>
        </div>
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <div className="max-w-md">
          {schemasLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : (
            <FormField
              as="select"
              label={t('bulkIssuance.schemaLabel')}
              value={selectedSchema}
              onChange={(e) => setSelectedSchema((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('bulkIssuance.schemaPlaceholder')}</option>
              {(schemas ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </FormField>
          )}
        </div>
      </div>

      {selectedSchema && csvRows.length === 0 && (
        <div
          className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 transition-colors ${
            isDragging ? 'border-primary bg-blue-50/50' : 'border-border-stroke bg-white'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFile}
          />
          <span className="material-symbols-outlined mb-3 text-text-faint" style={{ fontSize: 48 }}>upload_file</span>
          <p className="text-sm font-bold text-text-main">{t('bulkIssuance.uploadHeading')}</p>
          <p className="mt-1 text-xs text-text-faint">{t('bulkIssuance.uploadHint')}</p>
        </div>
      )}

      {csvRows.length > 0 && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="stat-card">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{t('bulkIssuance.stats.total')}</span>
              <span className="mt-2 block text-3xl font-bold text-text-main">{csvRows.length}</span>
            </div>
            <div className="stat-card">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{t('bulkIssuance.stats.valid')}</span>
              <span className="mt-2 block text-3xl font-bold text-emerald-600">{validCount}</span>
            </div>
            <div className="stat-card">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{t('bulkIssuance.stats.errors')}</span>
              <span className="mt-2 block text-3xl font-bold text-red-500">{errorCount}</span>
            </div>
            <div className="stat-card">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{t('bulkIssuance.stats.issued')}</span>
              <span className="mt-2 block text-3xl font-bold text-primary">{issuedCount}</span>
            </div>
          </div>

          {(isProcessing || completed) && (
            <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-text-muted">
                  {completed ? t('bulkIssuance.progress.complete') : t('bulkIssuance.progress.processing')}
                </span>
                <span className="text-xs font-bold text-primary">{progress}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${completed ? 'bg-emerald-500' : 'bg-primary'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              {completed && (
                <div className="mt-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 18 }}>check_circle</span>
                  <span className="text-sm font-bold text-emerald-700">
                    {t('bulkIssuance.progress.summary', { issued: issuedCount, errors: errorCount })}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-subtle bg-slate-50/50">
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('bulkIssuance.table.row')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('bulkIssuance.table.status')}</th>
                    {csvRows[0] && Object.keys(csvRows[0].data).map((key) => (
                      <th key={key} className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvRows.map((row) => (
                    <tr key={row.id} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                      <td className="px-4 py-3 text-xs font-bold text-text-muted">{row.id}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            row.status === 'issued' ? 'success' :
                            row.status === 'valid' ? 'info' :
                            row.status === 'error' ? 'error' : 'neutral'
                          }
                        >
                          {row.status}
                        </Badge>
                        {row.error && (
                          <p className="mt-1 text-[10px] text-red-500">{row.error}</p>
                        )}
                      </td>
                      {Object.values(row.data).map((val, i) => (
                        <td key={i} className="px-4 py-3 text-xs text-text-main">
                          {val ? (
                            <span className={val.startsWith('did:') ? 'font-mono text-[10px]' : ''}>{val}</span>
                          ) : (
                            <span className="text-red-400 italic">{t('bulkIssuance.errors.empty')}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {!completed && !isProcessing && (
            <div className="flex flex-wrap gap-3">
              <button className="btn-secondary" onClick={() => setCsvRows([])}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>upload_file</span>
                {t('bulkIssuance.uploadDifferent')}
              </button>
              <div className="w-full">
                <IssuerSignerPicker did={issuerDid} onChange={setSigner} />
                {signer?.kind === 'wallet' && validCount > 1 && (
                  <p className="mt-2 text-xs text-amber-700">
                    MetaMask will ask for one signature per credential ({validCount}). For large batches, sign with the DID's Ed25519 key instead.
                  </p>
                )}
              </div>
              <button className="btn-primary ml-auto" onClick={handleBulkIssue} disabled={validCount === 0 || !signer}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
                {t('bulkIssuance.issueCount', { count: validCount })}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
